import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useAction } from '../../context';
import { Failure, Field } from '../../bits';
import { useLocale, useT } from '../../../messages';
import type { CatalogItem } from '../../../wire';
import { priceText, textOf, useFreshItem } from './text';

/**
 * **What may be attached to this treatment.**
 *
 * The numbers on a link are the *attached* ones and are absolute, not deltas:
 * dermaplaning is $85 standing alone and $45 added to a facial (the owner's
 * ruling 4), which is why both boxes may legitimately read lower than the
 * add-on's own price and why leaving one blank means *the same as standing
 * alone* rather than nothing. The server resolves that, so what is shown is
 * always the number the link actually charges.
 *
 * **Unlink is the one destructive control on this tab**, and it is allowed for
 * the reason a length's delete is not: `appointment_services` snapshots the
 * add-on's name, price and minutes at the moment it was sold, so nothing in the
 * book points at the pairing.
 */
export function ItemAddOns({
  tenantId,
  item,
  siblings,
  locale,
  currency,
  commerceEditable,
  onChanged,
}: {
  tenantId: string;
  item: CatalogItem;
  /** Every other scheduled service, retired ones included — an add-on is an ordinary item. */
  siblings: CatalogItem[];
  locale: string;
  currency: string;
  commerceEditable: boolean;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const uiLocale = useLocale();
  const action = useAction();
  const [shown, setShown] = useFreshItem(item);
  const [pick, setPick] = useState('');
  const [price, setPrice] = useState('');
  const [minutes, setMinutes] = useState('');

  const linked = new Set(shown.add_ons.map((a) => a.item_id));
  const candidates = siblings.filter((s) => !linked.has(s.item_id));

  const cents = price.trim() === '' ? null : Math.round(Number(price) * 100);
  const mins = minutes.trim() === '' ? null : Number(minutes);
  const valid =
    pick !== '' &&
    (cents === null || (Number.isFinite(cents) && cents >= 0)) &&
    (mins === null || (Number.isInteger(mins) && mins >= 0));

  const link = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setAddOn(tenantId, item.item_id, {
        add_on_item_id: pick,
        // Both optional, and an omitted one is not a zero: absent says "as it
        // is sold alone", `0` says "adds no time at all".
        ...(cents === null ? {} : { price_cents: cents }),
        ...(mins === null ? {} : { duration_min: mins }),
      });
      setShown(answer.item);
    });
    if (ok) {
      setPick('');
      setPrice('');
      setMinutes('');
      onChanged();
    }
  };

  const unlink = async (addOnItemId: string): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.removeAddOn(tenantId, item.item_id, addOnItemId);
      setShown(answer.item);
    });
    if (ok) onChanged();
  };

  // Show only what applies: nothing attached and nothing this console may
  // attach on a live business is no block at all.
  if (!commerceEditable && shown.add_ons.length === 0) return null;

  return (
    <div className="catalog-block" data-add-ons={item.item_id}>
      <h4 className="catalog-block__title">{t('catalog.addOns')}</h4>
      {/* The live fence is said once, by the lengths above and the editor's top line. */}
      {commerceEditable ? <p className="muted">{t('catalog.extras.note')}</p> : null}

      {shown.add_ons.length === 0 ? (
        <p className="muted">{t('catalog.addOns.empty')}</p>
      ) : (
        <ul className="catalog-addons">
          {shown.add_ons.map((addOn) => (
            <li key={addOn.item_id} data-add-on={addOn.item_id}>
              <span>
                {addOn.name}
                {addOn.active ? null : (
                  <span className="muted"> · {t('catalog.addOns.paused')}</span>
                )}
              </span>
              <span className="muted">
                {t('catalog.extras.attached', {
                  price: priceText(addOn.price_cents, currency, uiLocale),
                  minutes: addOn.duration_min,
                })}
              </span>
              <button
                type="button"
                className="button button--quiet button--sm"
                aria-label={t('catalog.addOns.unlinkOf', { item: item.item_id, name: addOn.name })}
                disabled={!commerceEditable || action.busy}
                onClick={() => void unlink(addOn.item_id)}
              >
                {t('catalog.addOns.unlink')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {candidates.length === 0 || !commerceEditable ? null : (
        <details className="catalog-add">
          <summary>{t('catalog.extras.add')}</summary>
          <div className="catalog-fields">
            <Field label={t('catalog.extras.pickLabel')} hint={t('catalog.addOns.pickHint')}>
              <select
                aria-label={t('catalog.extras.pickOf', { item: item.item_id })}
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              >
                <option value="">{t('catalog.pick')}</option>
                {candidates.map((s) => (
                  <option key={s.item_id} value={s.item_id}>
                    {textOf(s.name, locale) || s.item_id}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('catalog.addOns.price')} hint={t('catalog.addOns.priceHint')}>
              <input
                inputMode="decimal"
                aria-label={t('catalog.addOns.priceOf', { item: item.item_id })}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </Field>
            <Field label={t('catalog.addOns.minutes')} hint={t('catalog.addOns.minutesHint')}>
              <input
                inputMode="numeric"
                aria-label={t('catalog.addOns.minutesOf', { item: item.item_id })}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </Field>
          </div>
          <div className="row-actions">
            <button
              type="button"
              className="button button--sm"
              aria-label={t('catalog.addOns.linkOf', { item: item.item_id })}
              disabled={!valid || action.busy}
              onClick={() => void link()}
            >
              {t('catalog.addOns.link')}
            </button>
          </div>
        </details>
      )}

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
    </div>
  );
}
